import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirebaseErrorMessage } from '../utils/firebaseErrors';
import { trackAccountCreated, identifyUser, trackUserLogin } from '../services/analytics';
import { trackWebSignUp } from '../services/unifiedAnalytics';
import { getPendingDealDoc, PENDING_DEAL_EVENT, dealAwareUrl } from '../services/branch';
import { Deal } from '../types/deal';
import CoBrandHeader from './CoBrandHeader';
import CalloutCard from './CalloutCard';
import LanguageSwitcher from './LanguageSwitcher';
import { sendVerificationEmail } from '../services/emailVerification';
import { createUserDocument, updateEmailSubscriptionPreference } from '../services/dealRepository';
import { validatePassword } from '../utils/passwordValidator';
import PasswordRequirements from './PasswordRequirements';
import { t } from '../i18n';
import { OnboardingSessionData } from '../types/onboardingSession';
import { loadOnboardingAnswers, clearOnboardingAnswers, isOnboardingCompleted } from '../services/onboardingStorage';
import { buildOnboardingSessionData } from '../services/buildOnboardingSession';
import { generatePlan } from '../services/planService';
import { getOnboardingEnabled } from '../services/firebase';
import { useFirebaseImageUrl } from '../hooks/useFirebaseImageUrl';
import { getPageImage, getPageLayout, layoutToStyle } from '../utils/dealImages';
import { localizeDeal } from '../utils/dealLocale';
import { useAwaitingDeal } from '../hooks/useAwaitingDeal';
import './Auth.css';
import './Promo.css';


const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pendingDeal, setPendingDeal] = useState<Deal | null>(null);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const { signup, loginWithGoogle, loginWithApple, currentUser, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const productImage = useFirebaseImageUrl(getPageImage(pendingDeal, 'signup'));
  const [heroLoaded, setHeroLoaded] = useState(false);
  const awaitingDeal = useAwaitingDeal(productImage, heroLoaded);
  const localizedDeal = localizeDeal(pendingDeal);

  // Read onboarding data from router state or rebuild from sessionStorage
  const [onboardingData] = useState<OnboardingSessionData | null>(() => {
    const stateData = (location.state as any)?.onboardingData;
    if (stateData) return stateData;
    // Fallback: rebuild from sessionStorage raw answers
    const savedAnswers = loadOnboardingAnswers();
    if (savedAnswers && Object.keys(savedAnswers).length > 0) {
      console.log('[Signup] Rebuilding onboarding data from sessionStorage');
      return buildOnboardingSessionData(savedAnswers);
    }
    return null;
  });

  // Password validation
  const passwordValidation = validatePassword(password);

  useEffect(() => {
    // Don't redirect mid-submit; route based on verification once settled
    if (currentUser && !processing) {
      navigate(currentUser.emailVerified ? '/offers' : '/verify-email');
    }
  }, [currentUser, navigate, processing]);

  // Guard: when onboarding is enabled, only allow signup if it's completed
  useEffect(() => {
    if (getOnboardingEnabled() && !currentUser && !onboardingData && !isOnboardingCompleted()) {
      navigate('/onboarding');
    }
  }, [currentUser, onboardingData, navigate]);

  useEffect(() => {
    // Pick up the pending deal from Branch — now, and again if it arrives async
    const sync = () => {
      const deal = getPendingDealDoc();
      if (deal) {
        setPendingDeal(deal);
        console.log('[Signup] Showing pending deal banner:', deal.name);
      }
    };
    sync();
    window.addEventListener(PENDING_DEAL_EVENT, sync);
    return () => window.removeEventListener(PENDING_DEAL_EVENT, sync);
  }, []);

  // Trigger plan generation after onboarding data is saved
  const triggerPlanGeneration = () => {
    if (!onboardingData) return;
    generatePlan({
      startDate: onboardingData.start_date,
      onboardingData: onboardingData.onboarding_data,
      raceEvent: onboardingData.race_event,
      isNewPlan: true,
    }).catch((err) => console.error('[Signup] Plan generation failed:', err));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password
    if (!passwordValidation.isValid) {
      setError(t('password.ensureRequirements'));
      return;
    }

    try {
      setError('');
      setLoading(true);
      // Guard the redirect effect while we finish account setup
      setProcessing(true);

      const userCredential = await signup(email, password);

      // Track account creation
      if (userCredential.user) {
        identifyUser(userCredential.user.uid, { email: userCredential.user.email || email });
        trackAccountCreated(userCredential.user.email || email, userCredential.user.uid);
        trackWebSignUp(pendingDeal?.id, userCredential.user.email || email, userCredential.user.uid);

        // Save user document with email preference + onboarding data in one write
        await createUserDocument(
          userCredential.user.uid,
          userCredential.user.email || email,
          marketingOptIn,
          onboardingData
        );

        // Fire-and-forget plan generation; onboarding data is now persisted
        triggerPlanGeneration();
        clearOnboardingAnswers();
      }

      // Send email verification via backend (stay logged in). Land back on the
      // co-branded deal page after verifying, not the app store / generic site.
      const result = await sendVerificationEmail(email, dealAwareUrl('/verify-email'));
      if (!result.success) {
        console.error('[Signup] Failed to send verification email:', result.error);
      }

      navigate('/verify-email');
    } catch (err: any) {
      // Check if email already exists
      if (err.code === 'auth/email-already-in-use') {
        console.log('[Signup] Email already in use, attempting to log in instead');
        try {
          const loginCredential = await login(email, password);

          if (loginCredential.user) {
            identifyUser(loginCredential.user.uid, { email: loginCredential.user.email || email });
            trackUserLogin(loginCredential.user.email || email, loginCredential.user.uid);

            if (marketingOptIn) {
              await updateEmailSubscriptionPreference(loginCredential.user.uid, true);
            }
          }

          navigate(loginCredential.user?.emailVerified ? '/offers' : '/verify-email');
        } catch (loginErr: any) {
          console.error('[Signup] Login attempt failed:', loginErr);
          setError(t('signup.emailAlreadyRegisteredPassword'));
          setProcessing(false);
        }
      } else {
        setError(getFirebaseErrorMessage(err));
        setProcessing(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      setError('');
      setLoading(true);
      const userCredential = await loginWithGoogle();

      // Google accounts are typically pre-verified
      if (userCredential.user) {
        identifyUser(userCredential.user.uid, { email: userCredential.user.email || '' });
        trackAccountCreated(userCredential.user.email || '', userCredential.user.uid);

        // Save user document with onboarding data
        await createUserDocument(
          userCredential.user.uid,
          userCredential.user.email || '',
          marketingOptIn,
          onboardingData
        );

        triggerPlanGeneration();
        clearOnboardingAnswers();
        navigate('/offers');
      }
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignup = async () => {
    try {
      setError('');
      setLoading(true);
      const userCredential = await loginWithApple();

      // Apple accounts are typically pre-verified
      if (userCredential.user) {
        identifyUser(userCredential.user.uid, { email: userCredential.user.email || '' });
        trackAccountCreated(userCredential.user.email || '', userCredential.user.uid);

        // Save user document with onboarding data
        await createUserDocument(
          userCredential.user.uid,
          userCredential.user.email || '',
          marketingOptIn,
          onboardingData
        );

        triggerPlanGeneration();
        clearOnboardingAnswers();
        navigate('/offers');
      }
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hp-page">
      <LanguageSwitcher />
      <div className="hp-split">
        <div className={`hp-hero${awaitingDeal ? ' hp-hero--awaiting' : ''}`}>
          {productImage && (
            <img src={productImage} alt={pendingDeal?.name || ''} className="hp-hero-fill" style={layoutToStyle(getPageLayout(pendingDeal, 'signup'))} onLoad={() => setHeroLoaded(true)} />
          )}
        </div>

        <div className="hp-panel">
          <div className="hp-panel-inner">
            <CoBrandHeader deal={pendingDeal} />

            <CalloutCard deal={localizedDeal} page="signup" />

            <div className="hp-heading">
              <h1 className="hp-headline">{localizedDeal?.headline || t('promo.welcomeHeadline')}</h1>
              <p className="hp-subtitle">
                {localizedDeal?.message || t('promo.signupSubtitle')}
              </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit} className="auth-form">
          <div className="input-field">
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('common.email')}
            />
          </div>

          <div className="input-field">
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              required
              placeholder={t('common.password')}
            />
          </div>

          <PasswordRequirements
            validation={passwordValidation}
            show={passwordFocused || (password.length > 0 && !passwordValidation.isValid)}
          />

          <div className="marketing-opt-in">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
              />
              <span className="checkmark"></span>
              <span className="checkbox-label">
                {t('signup.marketingOptIn')}
              </span>
            </label>
          </div>

          <button type="submit" disabled={loading || (password.length > 0 && !passwordValidation.isValid)} className="hp-cta">
            {loading ? t('signup.creatingAccount') : (localizedDeal?.ctaText || t('promo.getStarted'))}
          </button>
        </form>

        <div className="divider">
          <span>{t('signup.divider')}</span>
        </div>

        <div className="social-buttons">
          <button onClick={handleGoogleSignup} disabled={loading} className="btn-social">
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('signup.googleButton')}
          </button>

          <button onClick={handleAppleSignup} disabled={loading} className="btn-social">
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08l.15-.1zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="currentColor"/>
            </svg>
            {t('signup.appleButton')}
          </button>
        </div>

        <p style={{
          textAlign: 'center',
          fontSize: '14px',
          color: 'rgba(250, 253, 251, 0.7)',
          lineHeight: '1.5'
        }}>
          {t('signup.legalPrefix')}{' '}
          <a
            href="https://www.urunn.com/eula-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#E4FF78', textDecoration: 'underline' }}
          >
            {t('signup.termsLink')}
          </a>
          {' '}{t('signup.legalAnd')}{' '}
          <a
            href="https://www.urunn.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#E4FF78', textDecoration: 'underline' }}
          >
            {t('signup.privacyLink')}
          </a>
        </p>

            <p className="hp-secondary-link">
              {t('signup.haveAccount')} <Link to="/login">{t('signup.loginLink')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
